import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { parseSOR } from '@/api/sorApi';
import { fetchParameterRanges, type ParamRange } from '@/api/trainingDataApi';
import { useInputStore } from '@/stores/useInputStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { useUIStore } from '@/stores/useUIStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { BYD_VOIDED_PRESETS, BYD_SOLID_PRESETS } from '@/constants/bydPresets';
import { isByd, isValidBushingType, isValidMaterial, BYD_CORE_ID, BUSHING_TYPE_CONFIGS } from '@/constants/bushingTypes';
import {detectMissingSORParams,hasMissingParams,type MissingSORData,} from '@/features/inputs/MissingSORDialog';
import { mapRangesToFrontend } from '@/hooks/useParameterRanges';
import type { SORParseResult } from '@/types/sor';
import type { BushingType, BYDPreset } from '@/types/bushing';
import type { PackagingInputs as PackagingInputsType } from '@/stores/useInputStore';
import { geometryKeys } from '@/hooks/useGeometry';

interface UseSORUploadReturn {
  /** File name of the currently uploaded SOR (or null). */
  fileName: string | null;
  /** Whether parsing is in progress. */
  loading: boolean;
  /** Error message from the last parse attempt. */
  error: string | null;
  /** The last successful parse result. */
  parseResult: SORParseResult | null;
  /** SOR items for the combo box. */
  sorItems: string[];
  /** Handle a file drop / selection. */
  handleFile: (file: File) => void;
  /** Handle SOR combo box selection (for BYD presets). */
  handleSorSelect: (value: string | null) => void;
  /** Handle bushing type change — updates SOR items and may activate BYD mode. */
  handleBushingTypeChange: (type: BushingType) => void;
  /** Clear the uploaded SOR file without resetting parameter values. */
  clearFile: () => void;
  /** Missing SOR parameter data (null when no missing params). */
  missingData: MissingSORData | null;
  /** Whether the missing SOR dialog is open. */
  missingDialogOpen: boolean;
  /** Close the missing SOR dialog. */
  closeMissingDialog: () => void;
  /**
   * Called by BushingTypeChangeDialog when the user confirms discarding results.
   * Executes the pending type-change action that was deferred by the guard.
   */
  confirmBushingTypeChange: () => void;
}

/** Clamp a numeric value to the given range, returning null if input is null. */
function clampToRange(value: number | null, range?: ParamRange): number | null {
  if (value === null || !range) return value;
  if (value < range.min) return range.min;
  if (value > range.max) return range.max;
  return value;
}

/** Fetch packaging ranges for a bushing type (returns empty object on failure). */
async function fetchPackagingRanges(
  bushingType: BushingType,
): Promise<Partial<Record<keyof PackagingInputsType, ParamRange>>> {
  try {
    const raw = await fetchParameterRanges(bushingType);
    const mapped = mapRangesToFrontend(raw, bushingType);
    return mapped.packaging;
  } catch {
    return {};
  }
}

/** Apply a single SOR part's data to the input and config stores.
 *  Resets all fields first so stale values are never retained.
 *  If packagingRanges is provided, coreLength and outerSleeveLength
 *  are auto-clamped to the training-data min/max. */
function applyPartData(
  partData: SORParseResult['sorParts'][string],
  sorLabel?: string,
  packagingRanges?: Partial<Record<keyof PackagingInputsType, ParamRange>>,
) {
  const { setPackaging, setTargets, setAmplitudes, setSorString } =
    useInputStore.getState();
  const { setCoreMaterial, setOuterMaterial, setClampLoad, setAllowableYield, setSealRadial } =
    useConfigStore.getState();

  const bushingType = useBushingStore.getState().bushingType;
  const isRadialType = bushingType === 'A1' || bushingType === 'B1' || bushingType === 'C1';

  // Clamp core/sleeve lengths to training-data range if ranges are available
  const clampedCoreLength = clampToRange(
    partData.core_length ?? null,
    packagingRanges?.coreLength,
  );
  const clampedSleeveLength = clampToRange(
    partData.outer_sleeve_length ?? null,
    packagingRanges?.outerSleeveLength,
  );

  // Show Training Data Limit popup if any value was clamped
  const coreLengthClamped =
    partData.core_length != null &&
    packagingRanges?.coreLength &&
    clampedCoreLength !== partData.core_length;
  const sleeveLengthClamped =
    partData.outer_sleeve_length != null &&
    packagingRanges?.outerSleeveLength &&
    clampedSleeveLength !== partData.outer_sleeve_length;

  if (coreLengthClamped && packagingRanges?.coreLength) {
    useUIStore.getState().openLengthLimitDialog(
      'core length',
      packagingRanges.coreLength.min,
      packagingRanges.coreLength.max,
    );
  } else if (sleeveLengthClamped && packagingRanges?.outerSleeveLength) {
    useUIStore.getState().openLengthLimitDialog(
      'sleeve length',
      packagingRanges.outerSleeveLength.min,
      packagingRanges.outerSleeveLength.max,
    );
  }

  // Reset packaging — clear all fields, then overlay SOR values.
  setPackaging({
    outerSleeveLength: clampedSleeveLength,
    coreLength: clampedCoreLength,
    controlArmId: partData.control_arm_id ?? null,
    coreId: partData.core_id ?? null,
    hotOd: null,
    collarOd: partData.flange_od ?? null,
  });

  // Build targets from scratch — missing values become null (clearing stale data)
  const targets: Record<string, number | null> = {};
  const amplitudes: Record<string, number | null> = {};

  if (isRadialType) {
    // For A1/B1/C1: use radial_i for the single radial param (Cz)
    targets['Cz'] = partData.radial_i ?? null;
    amplitudes['Cz'] = partData.radial_i_amp ?? null;
  } else if (partData.detected_bushing_type === 'D1' || bushingType === 'D1') {
    // D1 has both Cy (radial void) and Cz (radial solid)
    targets['Cy'] = partData.radial_i ?? null;
    targets['Cz'] = partData.radial_ii ?? null;
    amplitudes['Cy'] = partData.radial_i_amp ?? null;
    amplitudes['Cz'] = partData.radial_ii_amp ?? null;
  } else {
    // BYD Voided has Cy + Cz, BYD Solid has Cz only
    targets['Cy'] = partData.radial_i ?? null;
    targets['Cz'] = partData.radial_ii ?? null;
    amplitudes['Cy'] = partData.radial_i_amp ?? null;
    amplitudes['Cz'] = partData.radial_ii_amp ?? null;
  }

  targets['Cx'] = partData.axial ?? null;
  amplitudes['Cx'] = partData.axial_amp ?? null;
  targets['Torsion'] = partData.torsion ?? null;
  amplitudes['Torsion'] = partData.torsion_amp ?? null;

  if (partData.detected_bushing_type === 'D1' || bushingType === 'D1') {
    targets['Conical Void'] = partData.conical_i ?? null;
    targets['Conical Solid'] = partData.conical_ii ?? null;
  } else {
    targets['Conical'] = partData.conical_i ?? null;
  }

  // Apply full target/amplitude records (replaces old values entirely)
  setTargets(targets);
  setAmplitudes(amplitudes);

  // Reset allowable yield
  setAllowableYield(260);

  // Apply materials and clamp load (clear if missing)
  let coreMat = null;
  if (partData.core_material && isValidMaterial(partData.core_material)) {
    setCoreMaterial(partData.core_material);
    coreMat = partData.core_material;
  } else {
    // Get current value from store if not in SOR
    coreMat = useConfigStore.getState().coreMaterial;
  }
  if (partData.outer_material && isValidMaterial(partData.outer_material)) {
    setOuterMaterial(partData.outer_material);
  }
  setClampLoad(partData.clamp_load ?? null);

  // Set sealing size: 0 if core material is steel, else 0.6
  setSealRadial(coreMat === 'Steel' ? 0 : 0.6);

  if (sorLabel) setSorString(sorLabel);
}

/** Map SOR parse result into the input store (uses first part). */
function applyParseResult(
  result: SORParseResult,
  packagingRanges?: Partial<Record<keyof PackagingInputsType, ParamRange>>,
) {
  const sorNumbers = Object.keys(result.sorParts);
  if (sorNumbers.length === 0) return;

  const firstPn = sorNumbers[0];
  const partData = result.sorParts[firstPn];
  if (!partData) return;

  applyPartData(partData, partData.sor_no || firstPn, packagingRanges);
}

/** Apply a BYD preset to the input store. */
function applyBydPreset(preset: BYDPreset) {
  const { setPackaging, setSleeveThickness } = useInputStore.getState();

  setPackaging({
    coreLength: preset.coreLength,
    outerSleeveLength: preset.sleeveLength,
    controlArmId: preset.controlArm,
    coreId: BYD_CORE_ID,
    coreOdMin: preset.coreOd,
    coreOdMax: preset.coreOd,
    hotOd: preset.hotOd,
  });
  setSleeveThickness(preset.sleeveThickness);
}

function getBydPresets(type: BushingType): BYDPreset[] {
  if (type === 'BYD EV Solid') return BYD_SOLID_PRESETS;
  if (type === 'BYD EV Voided') return BYD_VOIDED_PRESETS;
  return [];
}

/**
 * useSORUpload — Hook that manages SOR file upload, parsing, and
 * BYD preset logic.
 *
 * Handles:
 *  - File upload → API parse → store population
 *  - BYD preset auto-fill when bushing type is BYD
 *  - SOR combo box item management
 */
export function useSORUpload(): UseSORUploadReturn {
  const [fileName, setFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<SORParseResult | null>(null);
  const [sorItems, setSorItems] = useState<string[]>([]);
  const [missingData, setMissingData] = useState<MissingSORData | null>(null);
  const [missingDialogOpen, setMissingDialogOpen] = useState(false);

  const bushingType = useBushingStore((s) => s.bushingType);
  const queryClient = useQueryClient();

  // Global reset (RibbonBar's reset button) bumps this in useUIStore.
  // It has no direct handle on this hook's local state, so we clear
  // our own upload-related local state here in response.
  const resetToken = useUIStore((s) => s.resetToken);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setFileName(null);
    setParseResult(null);
    setSorItems([]);
    setError(null);
    setMissingData(null);
    setMissingDialogOpen(false);
  }, [resetToken]);

  /**
   * Pending bushing-type action deferred when generated samples exist.
   * Stored in a ref so it doesn't cause re-renders and is always fresh
   * when the confirmation dialog calls confirmBushingTypeChange().
   */
  const pendingTypeAction = useRef<(() => void | Promise<void>) | null>(null);

  /**
   * Applies a bushing type change, or defers it behind the discard-results
   * confirmation dialog if generated samples are present.
   */
  const applyOrGuardTypeChange = useCallback((action: () => void) => {
    const hasResults = useSampleStore.getState().generatedSamples.length > 0;
    if (hasResults) {
      pendingTypeAction.current = action;
      useUIStore.getState().openDialog('bushingTypeChangeDialog');
    } else {
      action();
    }
  }, []);

  /** Called by BushingTypeChangeDialog onConfirm — runs the deferred action. */
  const confirmBushingTypeChange = useCallback(() => {
    const action = pendingTypeAction.current;
    pendingTypeAction.current = null;
    action?.();
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      if (isByd(bushingType)) return; // SOR upload disabled for BYD

      setFileName(file.name);
      setLoading(true);
      setError(null);

      try {
        const result = await parseSOR(file);
        setParseResult(result);

        // Detect missing SOR parameters and show dialog if any
        const detected = detectMissingSORParams(result);
        if (hasMissingParams(detected)) {
          setMissingData(detected);
          setMissingDialogOpen(true);
        } else {
          setMissingData(null);
        }

        // Detect bushing type from SOR (first part)
        const sorNumbers = Object.keys(result.sorParts);
        let detectedType: string | undefined = undefined;
        if (sorNumbers.length > 0) {
          const firstPart = result.sorParts[sorNumbers[0]];
          detectedType = firstPart?.detected_bushing_type ?? undefined;
        }

        // Determine the effective bushing type for fetching ranges
        const effectiveType = (detectedType && isValidBushingType(detectedType))
          ? detectedType as BushingType
          : bushingType;

        // Pre-fetch packaging ranges for auto-clamping core/sleeve lengths
        const packagingRanges = await fetchPackagingRanges(effectiveType);

        // Action that applies all SOR data (bushing type + parameters + combo items)
        const applyAll = () => {
          // Switch bushing type if detected type is valid and different
          if (
            detectedType &&
            detectedType !== useBushingStore.getState().bushingType &&
            isValidBushingType(detectedType)
          ) {
            useBushingStore.getState().setBushingType(detectedType);
            const newConfig = BUSHING_TYPE_CONFIGS[detectedType];
            const currentThickness = useInputStore.getState().sleeveThickness;
            if (!newConfig.sleeveThickness.options.includes(currentThickness)) {
              useInputStore.getState().setSleeveThickness(newConfig.sleeveThickness.default);
            }
          }
          // Clear any existing generated samples
          useSampleStore.getState().clearGeneratedSamples();
          // Apply SOR parameter data (with auto-clamping)
          applyParseResult(result, packagingRanges);
          // Populate SOR combo items from sorParts keys
          if (sorNumbers.length > 0) {
            setSorItems(sorNumbers);
          }
        };

        // Guard behind discard dialog if samples exist
        const hasResults = useSampleStore.getState().generatedSamples.length > 0;
        if (hasResults) {
          pendingTypeAction.current = applyAll;
          useUIStore.getState().openDialog('bushingTypeChangeDialog');
        } else {
          applyAll();
        }
      } catch {
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [bushingType],
  );

  const handleSorSelect = useCallback(
    async (value: string | null) => {
      if (!value) return;

      useInputStore.getState().setSorString(value);

      // For BYD types, selecting a SOR item means selecting a preset
      if (isByd(bushingType)) {
        const presets = getBydPresets(bushingType);
        const preset = presets.find((p) => p.datasheet === value);
        if (preset) {
          applyBydPreset(preset);
        }
        return;
      }

      // For non-BYD: apply the selected part from the last parse result
      const partData = parseResult?.sorParts?.[value];
      if (partData) {
        const detectedType = partData.detected_bushing_type;

        // Determine effective bushing type for range fetching
        const effectiveType = (detectedType && isValidBushingType(detectedType))
          ? detectedType as BushingType
          : bushingType;

        // Pre-fetch packaging ranges for auto-clamping core/sleeve lengths
        const packagingRanges = await fetchPackagingRanges(effectiveType);

        // Action that applies bushing type change + all part parameters
        const applyAll = () => {
          if (
            detectedType &&
            detectedType !== useBushingStore.getState().bushingType &&
            isValidBushingType(detectedType)
          ) {
            useBushingStore.getState().setBushingType(detectedType);
            const newConfig = BUSHING_TYPE_CONFIGS[detectedType];
            const currentThickness = useInputStore.getState().sleeveThickness;
            if (!newConfig.sleeveThickness.options.includes(currentThickness)) {
              useInputStore.getState().setSleeveThickness(newConfig.sleeveThickness.default);
            }
          }
          // Clear any existing generated samples
          useSampleStore.getState().clearGeneratedSamples();
          // Apply all part parameter values (with auto-clamping)
          applyPartData(partData, value, packagingRanges);
        };

        // Guard behind discard dialog if samples exist
        const hasResults = useSampleStore.getState().generatedSamples.length > 0;
        if (hasResults) {
          pendingTypeAction.current = applyAll;
          useUIStore.getState().openDialog('bushingTypeChangeDialog');
        } else {
          applyAll();
        }
      }
    },
    [bushingType, parseResult],
  );

  const handleBushingTypeChange = useCallback(
    (type: BushingType) => {
      const prev = useBushingStore.getState().bushingType;

      const applyChange = () => {
        // Clear generated samples and geometry state BEFORE changing bushing type
        // to prevent stale geometry queries with the old sample + new type.
        useSampleStore.getState().clearGeneratedSamples();
        queryClient.removeQueries({ queryKey: geometryKeys.all });

        useBushingStore.getState().setBushingType(type);

        // Transition to BYD — seed presets, apply first preset
        if (isByd(type)) {
          const presets = getBydPresets(type);
          const items = presets.map((p) => p.datasheet);
          setSorItems(items);

          if (presets.length > 0) {
            applyBydPreset(presets[0]);
            useInputStore.getState().setSorString(presets[0].datasheet);
          }
        } else if (isByd(prev) && !isByd(type)) {
          // Transition away from BYD — clear and restore
          setSorItems([]);
          useInputStore.getState().resetInputs();

          // Restore sleeve thickness default for new type
          const newConfig = useBushingStore.getState().config;
          useInputStore
            .getState()
            .setSleeveThickness(newConfig.sleeveThickness.default);
        } else {
          // Non-BYD to non-BYD — just update sleeve thickness default
          const newConfig = useBushingStore.getState().config;
          useInputStore
            .getState()
            .setSleeveThickness(newConfig.sleeveThickness.default);
        }
      };

      applyOrGuardTypeChange(applyChange);
    },
    [applyOrGuardTypeChange],
  );

  const clearFile = useCallback(() => {
    setFileName(null);
    setParseResult(null);
    setSorItems([]);
    setError(null);
    setMissingData(null);
    setMissingDialogOpen(false);
    useInputStore.getState().resetInputs();
    useConfigStore.getState().resetConfig();
    useSampleStore.getState().clearGeneratedSamples();
    queryClient.removeQueries({ queryKey: geometryKeys.all });
  }, [queryClient]);

  const closeMissingDialog = useCallback(() => {
    setMissingDialogOpen(false);
  }, []);

  return {
    fileName,
    loading,
    error,
    parseResult,
    sorItems,
    handleFile,
    handleSorSelect,
    handleBushingTypeChange,
    clearFile,
    missingData,
    missingDialogOpen,
    closeMissingDialog,
    confirmBushingTypeChange,
  };
}
